import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { PresentationModeProvider } from "./context/PresentationModeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ExecutiveHeader } from "./components/ExecutiveHeader";
import { GlobalDashboard } from "./components/GlobalDashboard";
import { Chatbot } from "./components/Chatbot";
import ScrollToTop from "./components/ScrollToTop";
import OperationsPage from "./pages/OperationsPage";
import { FinancePage } from "./pages/FinancePage";
import { RevenueDetailPage } from "./pages/RevenueDetailPage";
import { EBITDADetailPage } from "./pages/EBITDADetailPage";
import { CashFlowDetailPage } from "./pages/CashFlowDetailPage";
import { HRPage } from "./pages/HRPage";
import { ESGPage } from "./pages/ESGPage";
import { MarketPage } from "./pages/MarketPage";
import AdminPage from "./pages/AdminPage";
import LoginPage from "./pages/LoginPage";
import { Toaster } from "./components/ui/sonner";

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    // Show loading spinner while checking auth
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-8 w-8 border-4 border-[#0A192F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AppContent = () => {
  const [selectedYear, setSelectedYear] = useState(2026);
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  
  // Check if we're on login page
  const isLoginPage = location.pathname === '/login';
  
  // Check if we're on main dashboard only
  const showHeader = location.pathname === '/' && isAuthenticated;
  
  // Check if we're on admin page (no chatbot there)
  const isAdminPage = location.pathname === '/admin';

  // If on login page and already authenticated, redirect to dashboard
  if (isLoginPage && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Scroll to top on route change */}
      <ScrollToTop />
      
      {/* Only show header on main dashboard when authenticated */}
      {showHeader && (
        <ExecutiveHeader 
          year={selectedYear} 
          onYearChange={setSelectedYear} 
        />
      )}
      
      <Routes>
        {/* Public route - Login */}
        <Route path="/login" element={<LoginPage />} />
        
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <GlobalDashboard year={selectedYear} onYearChange={setSelectedYear} />
          </ProtectedRoute>
        } />
        <Route path="/operations" element={
          <ProtectedRoute>
            <OperationsPage />
          </ProtectedRoute>
        } />
        <Route path="/finance" element={
          <ProtectedRoute>
            <FinancePage year={selectedYear} />
          </ProtectedRoute>
        } />
        <Route path="/finance/revenue" element={
          <ProtectedRoute>
            <RevenueDetailPage year={selectedYear} />
          </ProtectedRoute>
        } />
        <Route path="/finance/ebitda" element={
          <ProtectedRoute>
            <EBITDADetailPage year={selectedYear} />
          </ProtectedRoute>
        } />
        <Route path="/finance/cashflow" element={
          <ProtectedRoute>
            <CashFlowDetailPage year={selectedYear} />
          </ProtectedRoute>
        } />
        <Route path="/finance/capex" element={
          <ProtectedRoute>
            <FinancePage year={selectedYear} />
          </ProtectedRoute>
        } />
        <Route path="/hr" element={
          <ProtectedRoute>
            <HRPage />
          </ProtectedRoute>
        } />
        <Route path="/esg" element={
          <ProtectedRoute>
            <ESGPage />
          </ProtectedRoute>
        } />
        <Route path="/market" element={
          <ProtectedRoute>
            <MarketPage />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } />
        
        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      
      {/* Chatbot available on all pages except admin and login */}
      {!isAdminPage && !isLoginPage && isAuthenticated && <Chatbot />}
      
      {/* Toast notifications */}
      <Toaster position="top-right" />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <PresentationModeProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </PresentationModeProvider>
    </LanguageProvider>
  );
}

export default App;
